/**
 * Left rail — collapsible Wanted progressive + paytable combos with real art.
 * Phone: edge tab → slide-over drawer. Desktop/web: docked panel or min tab.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FREE_SPIN_BOUNTY,
  JACKPOT_ORDER,
  JACKPOT_TIERS,
  MAX_LINES,
  REEL_COUNT,
  ROW_COUNT,
  SLOT_MATH,
  getPaytable,
  type JackpotTierId,
} from "@shared/slot-engine";
import { SlotSymbol, type SymbolId } from "@/components/slot-symbols";
import { slotsAudio } from "@/lib/slots-audio";

const LS_OPEN = "poker_slots_left_rail_open_v1";
const LS_SECTION = "poker_slots_left_rail_section_v1";

export type LeftRailSection = "wanted" | "combos" | "wins";

export type WantedHitStats = Record<JackpotTierId, number>;

export function emptyWantedHits(): WantedHitStats {
  return {
    jp_mini: 0,
    jp_medium: 0,
    jp_large: 0,
    jp_grand: 0,
  };
}

type PayEntry = {
  id: string;
  name: string;
  pays3: number;
  pays4: number;
  pays5: number;
};

function readOpenDefault(): boolean {
  try {
    const v = localStorage.getItem(LS_OPEN);
    if (v === "0") return false;
    if (v === "1") return true;
    // Desktop default open-ish tab preference: start collapsed so glass owns space
    return false;
  } catch {
    return false;
  }
}

function readSection(): LeftRailSection {
  try {
    const v = localStorage.getItem(LS_SECTION);
    if (v === "wanted" || v === "combos" || v === "wins") return v;
  } catch {
    /* ignore */
  }
  return "wanted";
}

export function SlotsLeftRail({
  open,
  onOpenChange,
  wantedHits,
  sessionWon,
  sessionWager,
  totalBet,
  lastWin,
  onOpenNovel,
  onOpenLegal,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wantedHits: WantedHitStats;
  sessionWon: number;
  sessionWager: number;
  totalBet: number;
  lastWin: number;
  onOpenNovel?: () => void;
  onOpenLegal?: () => void;
}) {
  const [section, setSection] = useState<LeftRailSection>(readSection);
  const payTable = useMemo(() => getPaytable() as PayEntry[], []);

  useEffect(() => {
    try {
      localStorage.setItem(LS_OPEN, open ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [open]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_SECTION, section);
    } catch {
      /* ignore */
    }
  }, [section]);

  const toggle = useCallback(() => {
    slotsAudio.unlock();
    onOpenChange(!open);
  }, [open, onOpenChange]);

  const conquest =
    sessionWager > 0
      ? Math.min(
          100,
          Math.round((sessionWon / Math.max(sessionWager, 1)) * 40 + 12),
        )
      : 8;

  const totalWantedHits = JACKPOT_ORDER.reduce(
    (s, id) => s + (wantedHits[id] || 0),
    0,
  );

  return (
    <>
      {/* Always-visible left edge tab when collapsed */}
      <button
        type="button"
        className={`slots-rail-tab ${open ? "is-open" : ""}`}
        onClick={toggle}
        aria-expanded={open}
        aria-controls="slots-left-rail-panel"
        title={open ? "Minimize info panel" : "Open Wanted progressive & paytable"}
      >
        <span className="slots-rail-tab__stack" aria-hidden>
          {JACKPOT_ORDER.map((id) => (
            <img
              key={id}
              src={JACKPOT_TIERS[id].icon}
              alt=""
              className="slots-rail-tab__ico"
              draggable={false}
            />
          ))}
        </span>
        <span className="slots-rail-tab__label">
          {open ? "HIDE" : "INFO"}
        </span>
        <span className="slots-rail-tab__chev" aria-hidden>
          {open ? "‹" : "›"}
        </span>
      </button>

      {/* Scrim on small screens when open */}
      <AnimatePresence>
        {open ? (
          <motion.button
            key="rail-scrim"
            type="button"
            className="slots-rail-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-label="Close panel"
            onClick={() => onOpenChange(false)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {open ? (
          <motion.aside
            key="rail-panel"
            id="slots-left-rail-panel"
            className="slots-left-rail"
            role="complementary"
            aria-label="Wanted progressive and paytable"
            initial={{ x: "-105%" }}
            animate={{ x: 0 }}
            exit={{ x: "-105%" }}
            transition={{ type: "spring", stiffness: 380, damping: 34 }}
          >
            <header className="slots-left-rail__head">
              <div className="min-w-0">
                <div className="slots-left-rail__kicker">Ethereal Treasure</div>
                <h2 className="slots-left-rail__title">MOST WANTED</h2>
                <p className="slots-left-rail__sub">
                  {REEL_COUNT}×{ROW_COUNT} · {MAX_LINES} lines · RTP{" "}
                  {(SLOT_MATH.targetRtp * 100).toFixed(0)}%
                </p>
              </div>
              <button
                type="button"
                className="slots-left-rail__min"
                onClick={() => onOpenChange(false)}
                aria-label="Minimize to left tab"
                title="Minimize"
              >
                ‹ Min
              </button>
            </header>

            <nav className="slots-left-rail__nav" aria-label="Panel sections">
              {(
                [
                  { id: "wanted" as const, label: "Wanted" },
                  { id: "combos" as const, label: "Combos" },
                  { id: "wins" as const, label: "Wins" },
                ] as const
              ).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={section === t.id ? "is-on" : ""}
                  onClick={() => {
                    slotsAudio.unlock();
                    setSection(t.id);
                  }}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            <div className="slots-left-rail__body">
              {section === "wanted" && (
                <div className="slots-rail-wanted">
                  <div className="slots-rail-progress-card">
                    <div className="slots-rail-progress-card__top">
                      <span>Sea conquest</span>
                      <span>{conquest}%</span>
                    </div>
                    <div className="slots-rail-meter" aria-hidden>
                      <div
                        className="slots-rail-meter__fill"
                        style={{ width: `${conquest}%` }}
                      />
                    </div>
                    <p className="slots-rail-hint">
                      Session wins chart Racalvin&apos;s route · wagered{" "}
                      {Math.round(sessionWager)} · won {Math.round(sessionWon)}
                    </p>
                  </div>

                  <p className="slots-rail-section-label">
                    Bounty progressive · {totalWantedHits} hit
                    {totalWantedHits === 1 ? "" : "s"} this session
                  </p>

                  <ul className="slots-rail-wanted-list">
                    {JACKPOT_ORDER.map((id) => {
                      const t = JACKPOT_TIERS[id];
                      const hits = wantedHits[id] || 0;
                      const est = Math.round(totalBet * t.mult);
                      return (
                        <li
                          key={id}
                          className={`slots-rail-wanted-card ${hits > 0 ? "is-hot" : ""}`}
                          style={{ ["--tier" as string]: t.color }}
                        >
                          <img
                            src={t.poster || t.icon}
                            alt={t.name}
                            className="slots-rail-wanted-card__art"
                            draggable={false}
                          />
                          <div className="slots-rail-wanted-card__meta">
                            <div className="slots-rail-wanted-card__name">
                              {t.name}
                            </div>
                            <div className="slots-rail-wanted-card__title">
                              {t.title}
                            </div>
                            <div className="slots-rail-wanted-card__pays">
                              <strong>{t.mult}×</strong> bet
                              {totalBet > 0 ? (
                                <span className="slots-rail-wanted-card__est">
                                  ≈ {est} GBUX
                                </span>
                              ) : null}
                            </div>
                            <div className="slots-rail-wanted-card__hits">
                              {hits > 0
                                ? `${hits} session hit${hits > 1 ? "s" : ""}`
                                : "3+ posters anywhere"}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="slots-rail-malachar">
                    <img
                      src={FREE_SPIN_BOUNTY.poster || FREE_SPIN_BOUNTY.icon}
                      alt={FREE_SPIN_BOUNTY.name}
                      draggable={false}
                    />
                    <div>
                      <strong>{FREE_SPIN_BOUNTY.name}</strong>
                      <div className="opacity-70 text-[11px] leading-snug">
                        {FREE_SPIN_BOUNTY.title}
                      </div>
                      <div className="slots-rail-hint mt-1">
                        Free voyage · 3/4/5 → 5/7/10 spins · no Wanted pays
                        on free
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="slots-rail-cta"
                    onClick={() => {
                      slotsAudio.unlock();
                      onOpenNovel?.();
                    }}
                  >
                    Open Wanted novel
                  </button>
                </div>
              )}

              {section === "combos" && (
                <div className="slots-rail-combos">
                  <p className="slots-rail-section-label">
                    Symbol pays · 3 / 4 / 5 of a kind (× bet/line)
                  </p>
                  <ul className="slots-rail-paylist">
                    {payTable.map((sym) => (
                      <li key={sym.id} className="slots-rail-payrow">
                        <div className="slots-rail-payrow__ico">
                          <SlotSymbol id={sym.id as SymbolId} />
                        </div>
                        <div className="slots-rail-payrow__mid">
                          <span className="slots-rail-payrow__name">
                            {sym.name}
                          </span>
                        </div>
                        <div className="slots-rail-payrow__pays">
                          <span>
                            3<span>{sym.pays3}</span>
                          </span>
                          <span>
                            4<span>{sym.pays4}</span>
                          </span>
                          <span>
                            5<span>{sym.pays5}</span>
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <p className="slots-rail-section-label mt-3">
                    Special symbols
                  </p>
                  <div className="slots-rail-feature-grid">
                    <div className="slots-rail-feature">
                      <div className="slots-rail-payrow__ico">
                        <SlotSymbol id="wild" />
                      </div>
                      <div>
                        <strong>Wild</strong>
                        <p>Subs pay symbols · mult cap {SLOT_MATH.wildMultCap}×</p>
                      </div>
                    </div>
                    <div className="slots-rail-feature">
                      <div className="slots-rail-payrow__ico">
                        <SlotSymbol id="bonus" />
                      </div>
                      <div>
                        <strong>Bonus seal</strong>
                        <p>3+ anywhere → Ethereal Vault spins</p>
                      </div>
                    </div>
                    <div className="slots-rail-feature">
                      <img
                        src={FREE_SPIN_BOUNTY.icon}
                        alt=""
                        className="slots-rail-payrow__img"
                        draggable={false}
                      />
                      <div>
                        <strong>Malachar</strong>
                        <p>Scatter · free voyage charts</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {section === "wins" && (
                <div className="slots-rail-wins">
                  <div className="slots-rail-progress-card">
                    <div className="slots-rail-progress-card__top">
                      <span>Last win</span>
                      <span className="text-amber-200 font-mono">
                        {lastWin > 0 ? `+${lastWin}` : "—"}
                      </span>
                    </div>
                    <div className="slots-rail-progress-card__top">
                      <span>Total bet</span>
                      <span>{totalBet} GBUX</span>
                    </div>
                    <div className="slots-rail-progress-card__top">
                      <span>Session</span>
                      <span>
                        {Math.round(sessionWon)} / {Math.round(sessionWager)}
                      </span>
                    </div>
                  </div>

                  <p className="slots-rail-section-label">How wins evaluate</p>
                  <ul className="slots-rail-rules">
                    <li>
                      <strong>Paylines</strong> — left → right from reel 1 · need
                      3+ matching pay symbols (or wilds)
                    </li>
                    <li>
                      <strong>Match mults</strong> — 3× / 4× / 5× ={" "}
                      {SLOT_MATH.matchMults.join(" / ")}× symbol value
                    </li>
                    <li>
                      <strong>Wanted</strong> — 3+ posters anywhere · paid /
                      vault / special only
                    </li>
                    <li>
                      <strong>Stack</strong> — 4 posters 1.25× · 5 = 1.5× · 7+ =
                      2×
                    </li>
                    <li>
                      <strong>Max win</strong> — {SLOT_MATH.maxWinMultiple}×
                      stake per spin · house edge ~{" "}
                      {(SLOT_MATH.houseEdge * 100).toFixed(0)}%
                    </li>
                  </ul>

                  <p className="slots-rail-section-label">Wanted ladder</p>
                  <div className="slots-rail-ladder">
                    {JACKPOT_ORDER.map((id) => {
                      const t = JACKPOT_TIERS[id];
                      return (
                        <div key={id} className="slots-rail-ladder__row">
                          <img src={t.icon} alt="" draggable={false} />
                          <span>{t.shortName}</span>
                          <strong style={{ color: t.color }}>{t.mult}×</strong>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="slots-rail-cta slots-rail-cta--ghost"
                    onClick={() => {
                      onOpenChange(false);
                      onOpenLegal?.();
                    }}
                  >
                    Math · privacy · terms
                  </button>
                </div>
              )}
            </div>
          </motion.aside>
        ) : null}
      </AnimatePresence>
    </>
  );
}

/** Bootstrap open state from localStorage for parent */
export function getLeftRailOpenPref(): boolean {
  return readOpenDefault();
}
